import axios from 'axios';
import React, { useState } from 'react';
import { useHistory } from '@useHistory/useHistoryCompat';
import { getErrorFromAxiosError, getErrorMessageFromObj } from '../../../core/utils/request';
import { HdResolvedComponent } from '../../../routing/components/resolved-component/HdResolvedComponent';
import { HdPane } from '../../../components';
import HdButton from '../../../button/components/button/HdButton';
import useUserService from '../../hooks/useUserService';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import { HdAuth } from '../auth-layout/AuthLayout';
import { FeaturedCustomers } from '../testimonial-section/TestimonialSection';
import { useAuthGuard } from '../../hooks/useAuthGuard';
import { useFeaturedCustomers } from '../../hooks/useFeaturedCustomer';

declare const __WEBSITE_URL__: string;

export interface SubscribeMarketplaceProps {
  featuredCustomers: FeaturedCustomers;
}

const dataIdGenerator = getDataIdGenerator('subscribe-aws-marketplace');

export function SubscribeMarketplace() {
  const { getFeaturedCustomers } = useFeaturedCustomers();
  const { executeAuthGuard } = useAuthGuard();

  return (
    <HdResolvedComponent
      Component={SubscribeMarketplaceInner}
      resolve={{
        featuredCustomers: getFeaturedCustomers
      }}
      canActivate={[executeAuthGuard]}
    />
  );
}

export function SubscribeMarketplaceInner({ featuredCustomers }: SubscribeMarketplaceProps) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string>(null);

  const history = useHistory();

  const { user } = useUserService();

  const accountId = new URLSearchParams(history.location.search).get('account_id');
  const provider = new URLSearchParams(history.location.search).get('marketplace_provider');

  const subscriptionMetaURL = `${__WEBSITE_URL__}/auth/subscription-meta`;

  const cancelClick = () => {
    setCancelling(true);
    setCancelError(null);

    axios
      .delete(subscriptionMetaURL, {
        headers: { Anonymous: 'True' },
        withCredentials: true
      })
      .then(
        () => {
          setCancelling(false);
          history.replace('/');
        },
        error => {
          const err = getErrorFromAxiosError(error);
          setCancelError(getErrorMessageFromObj(err));
          setCancelling(false);
        }
      );
  };

  const subscribeClick = () => {
    setSubmitting(true);
    setSubmitError(null);

    axios
      .post(
        subscriptionMetaURL,
        {
          team_id: user.teamId
        },
        {
          headers: { Anonymous: 'True' },
          withCredentials: true
        }
      )
      .then(
        () => {
          setSubmitting(false);
          history.replace('/');
        },
        error => {
          const err = getErrorFromAxiosError(error);
          setSubmitError(getErrorMessageFromObj(err));
          setSubmitting(false);
        }
      );
  };

  return (
    <HdAuth
      heading={`Subscribe using ${provider} Marketplace?`}
      featuredCustomers={featuredCustomers}
    >
      <div className='text-subheading-1 text-secondary text-center'>
        {`By subscribing, your Hevo account will be linked to ${provider} Marketplace with account ID`}
        <b className='text-default'>{accountId}</b>
      </div>

      {submitError || cancelError ? (
        <HdPane
          dataId={dataIdGenerator('form-error')}
          className='mt-5 w-100'
          variant='error-faded'
          icon='error-filled'
          iconClasses='text-error'
          disableHide
        >
          <div className='text-default'>{submitError || cancelError}</div>
        </HdPane>
      ) : null}

      <div className='center-flex-row mt-5'>
        <HdButton
          dataId={dataIdGenerator('cancel')}
          variation='outline'
          showProgress={cancelling}
          disabled={cancelling}
          onClick={cancelClick}
        >
          Cancel
        </HdButton>

        <HdButton
          dataId={dataIdGenerator('subscribe')}
          className='ml-4'
          showProgress={submitting}
          disabled={submitting}
          onClick={subscribeClick}
        >
          {submitting ? 'Subscribing' : 'Subscribe'}
        </HdButton>
      </div>
    </HdAuth>
  );
}
